import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  RECOMMENDATION_ENGINE_DIVERSITY_DIMENSIONS,
  RecommendationEngineDiversityDimension,
  RecommendationEngineRerankKind,
  RecommendationEngineRerankRule,
} from "../../../../types/recommendationEngine";
import {
  DIVERSITY_DIMENSION_LABELS,
  RERANK_FIELD_TOOLTIPS,
  RERANK_RULE_HELP,
  RERANK_RULE_LABELS,
  RERANK_RULES_INTRO,
} from "../copy";
import InfoTooltip from "./InfoTooltip";
import { engineTokens } from "../theme";

type Props = {
  rules: RecommendationEngineRerankRule[];
  onChange: (next: RecommendationEngineRerankRule[]) => void;
};

const RERANK_KIND_ORDER: RecommendationEngineRerankKind[] = [
  "diversity_cap",
  "boost_flyer_item_ids",
  "demote_flyer_item_ids",
];

const createRule = (
  kind: RecommendationEngineRerankKind,
): RecommendationEngineRerankRule => {
  switch (kind) {
    case "diversity_cap":
      return { kind, dimension: "merchant", maxPerGroup: 3 };
    case "boost_flyer_item_ids":
      return { kind, flyerItemIds: [], multiplier: 1.2 };
    case "demote_flyer_item_ids":
      return { kind, flyerItemIds: [], multiplier: 0.8 };
    default:
      return { kind: "diversity_cap", dimension: "merchant", maxPerGroup: 3 };
  }
};

const parseIdList = (value: string): string[] =>
  value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const RerankRulesEditor = ({ rules, onChange }: Props) => {
  const [pendingKind, setPendingKind] =
    useState<RecommendationEngineRerankKind>("diversity_cap");

  const updateRule = (index: number, next: RecommendationEngineRerankRule) =>
    onChange(rules.map((r, i) => (i === index ? next : r)));

  const removeRule = (index: number) =>
    onChange(rules.filter((_, i) => i !== index));

  const addRule = () => {
    onChange([...rules, createRule(pendingKind)]);
  };

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        {RERANK_RULES_INTRO}
      </Typography>

      {rules.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5 }}
        >
          No rerank rules configured. The ranked list is returned as-is.
        </Typography>
      )}

      {rules.map((rule, index) => (
        <Box
          key={`${rule.kind}-${index}`}
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
            border: `1px solid ${engineTokens.border}`,
            borderRadius: 1.5,
            p: 1.25,
            mb: 1,
            backgroundColor: engineTokens.bgPaper,
          }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {RERANK_RULE_LABELS[rule.kind]}
            </Typography>
            <InfoTooltip title={RERANK_RULE_HELP[rule.kind]} />
          </Box>

          {rule.kind === "diversity_cap" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Dimension</InputLabel>
                  <Select
                    label="Dimension"
                    value={rule.dimension}
                    onChange={(e) =>
                      updateRule(index, {
                        ...rule,
                        dimension: e.target
                          .value as RecommendationEngineDiversityDimension,
                      })
                    }
                  >
                    {RECOMMENDATION_ENGINE_DIVERSITY_DIMENSIONS.map((d) => (
                      <MenuItem key={d} value={d}>
                        {DIVERSITY_DIMENSION_LABELS[d]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <InfoTooltip title={RERANK_FIELD_TOOLTIPS.dimension} />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TextField
                  size="small"
                  type="number"
                  label="Max per group"
                  value={rule.maxPerGroup}
                  onChange={(e) =>
                    updateRule(index, {
                      ...rule,
                      maxPerGroup: Number(e.target.value),
                    })
                  }
                  sx={{ width: 160 }}
                />
                <InfoTooltip title={RERANK_FIELD_TOOLTIPS.maxPerGroup} />
              </Box>
            </>
          )}

          {(rule.kind === "boost_flyer_item_ids" ||
            rule.kind === "demote_flyer_item_ids") && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  minWidth: 320,
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  label="Flyer item IDs (comma-separated)"
                  value={rule.flyerItemIds.join(", ")}
                  onChange={(e) =>
                    updateRule(index, {
                      ...rule,
                      flyerItemIds: parseIdList(e.target.value),
                    })
                  }
                />
                <InfoTooltip title={RERANK_FIELD_TOOLTIPS.flyerItemIds} />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TextField
                  size="small"
                  type="number"
                  label="Multiplier"
                  value={rule.multiplier}
                  onChange={(e) =>
                    updateRule(index, {
                      ...rule,
                      multiplier: Number(e.target.value),
                    })
                  }
                  inputProps={{ step: 0.05 }}
                  sx={{ width: 140 }}
                />
                <InfoTooltip title={RERANK_FIELD_TOOLTIPS.multiplier} />
              </Box>
            </>
          )}

          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            aria-label={`Remove ${RERANK_RULE_LABELS[rule.kind]} rule`}
            onClick={() => removeRule(index)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          mt: 1.5,
          flexWrap: "wrap",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Add rerank rule</InputLabel>
          <Select
            label="Add rerank rule"
            value={pendingKind}
            onChange={(e) =>
              setPendingKind(
                e.target.value as RecommendationEngineRerankKind,
              )
            }
          >
            {RERANK_KIND_ORDER.map((kind) => (
              <MenuItem key={kind} value={kind}>
                <Box>
                  <Typography variant="body2">
                    {RERANK_RULE_LABELS[kind]}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "normal" }}
                  >
                    {RERANK_RULE_HELP[kind]}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addRule}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};

export default RerankRulesEditor;
